import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { TrendingUpIcon, StarIcon, Clock3Icon, ImageIcon, MessageCircleIcon, CodeIcon, ChevronLeftIcon, ChevronRightIcon, MenuIcon, XIcon } from 'lucide-react';
export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Get current filter from URL
  const searchParams = new URLSearchParams(location.search);
  const currentFilter = searchParams.get('filter');
  const handleFilter = (filter: string) => {
    const basePath = location.pathname === '/' ? '/' : '/apps';
    navigate(`${basePath}?filter=${filter}`);
    setIsMobileMenuOpen(false);
  };
  const MenuItem = ({
    icon: Icon,
    label,
    onClick,
    color = 'text-gray-700',
    filterId
  }: any) => {
    const isActive = currentFilter === filterId;
    return <button onClick={onClick} className={`w-full flex items-center p-2 rounded-md transition-colors ${isActive ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-223">
        <Icon className={`h-5 w-5 ${isActive ? 'text-indigo-600' : color}`} data-id="element-224" />
        <span className="ml-3" data-id="element-225">{label}</span>
      </button>;
  };
  // Mobile menu overlay
  const MobileMenu = () => <div className={`md:hidden fixed inset-0 z-50 ${isMobileMenuOpen ? 'block' : 'hidden'}`} data-id="element-226">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)} data-id="element-227" />
      <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out" data-id="element-228">
        <div className="p-4" data-id="element-229">
          <div className="flex justify-between items-center mb-6" data-id="element-230">
            <h2 className="text-lg font-semibold" data-id="element-231">Browse</h2>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2" data-id="element-232">
              <XIcon className="h-5 w-5 text-gray-500" data-id="element-233" />
            </button>
          </div>
          <div className="space-y-6" data-id="element-234">
            {/* Browse Section */}
            <div data-id="element-235">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-236">
                Discover
              </h3>
              <div className="space-y-1" data-id="element-237">
                <MenuItem icon={TrendingUpIcon} label="Popular" onClick={() => handleFilter('popular')} color="text-orange-500" filterId="popular" data-id="element-238" />
                <MenuItem icon={StarIcon} label="Featured" onClick={() => handleFilter('featured')} color="text-amber-400" filterId="featured" data-id="element-239" />
                <MenuItem icon={Clock3Icon} label="New" onClick={() => handleFilter('new')} color="text-blue-500" filterId="new" data-id="element-240" />
              </div>
            </div>
            {/* Categories Section */}
            <div data-id="element-241">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-242">
                Categories
              </h3>
              <div className="space-y-1" data-id="element-243">
                <MenuItem icon={ImageIcon} label="Image Generation" onClick={() => handleFilter('image')} color="text-purple-500" filterId="image" data-id="element-244" />
                <MenuItem icon={MessageCircleIcon} label="Chat & Conversation" onClick={() => handleFilter('chat')} color="text-green-500" filterId="chat" data-id="element-245" />
                <MenuItem icon={CodeIcon} label="Code Generation" onClick={() => handleFilter('code')} color="text-red-500" filterId="code" data-id="element-246" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
  // Desktop sidebar
  const DesktopSidebar = () => <aside className={`border-r border-gray-200 min-h-screen transition-all duration-300 hidden md:block ${isCollapsed ? 'w-16' : 'w-64'}`} data-id="element-247">
      <div className="p-4" data-id="element-248">
        <div className="flex justify-end mb-4" data-id="element-249">
          <button onClick={() => setIsCollapsed(!isCollapsed)} className="p-1.5 rounded-md hover:bg-gray-100" data-id="element-250">
            {isCollapsed ? <ChevronRightIcon className="h-4 w-4 text-gray-600" data-id="element-251" /> : <ChevronLeftIcon className="h-4 w-4 text-gray-600" data-id="element-252" />}
          </button>
        </div>
        <div className="mb-6" data-id="element-253">
          <h2 className={`text-sm font-medium text-gray-500 mb-2 ${isCollapsed ? 'sr-only' : ''}`} data-id="element-254">
            Browse
          </h2>
          <ul className="space-y-2" data-id="element-255">
            <li data-id="element-256">
              <button onClick={() => handleFilter('popular')} className={`w-full flex items-center p-2 rounded-md transition-colors ${isCollapsed ? 'justify-center' : ''} ${currentFilter === 'popular' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-257">
                <TrendingUpIcon className={`h-5 w-5 ${currentFilter === 'popular' ? 'text-indigo-600' : 'text-orange-500'}`} data-id="element-258" />
                {!isCollapsed && <span className="ml-3" data-id="element-259">Popular</span>}
              </button>
            </li>
            <li data-id="element-260">
              <button onClick={() => handleFilter('featured')} className={`w-full flex items-center p-2 rounded-md transition-colors ${isCollapsed ? 'justify-center' : ''} ${currentFilter === 'featured' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-261">
                <StarIcon className={`h-5 w-5 ${currentFilter === 'featured' ? 'text-indigo-600' : 'text-amber-400'}`} data-id="element-262" />
                {!isCollapsed && <span className="ml-3" data-id="element-263">Featured</span>}
              </button>
            </li>
            <li data-id="element-264">
              <button onClick={() => handleFilter('new')} className={`w-full flex items-center p-2 rounded-md transition-colors ${isCollapsed ? 'justify-center' : ''} ${currentFilter === 'new' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-265">
                <Clock3Icon className={`h-5 w-5 ${currentFilter === 'new' ? 'text-indigo-600' : 'text-blue-500'}`} data-id="element-266" />
                {!isCollapsed && <span className="ml-3" data-id="element-267">New</span>}
              </button>
            </li>
          </ul>
        </div>
        <div data-id="element-268">
          <h2 className={`text-sm font-medium text-gray-500 mb-2 ${isCollapsed ? 'sr-only' : ''}`} data-id="element-269">
            Categories
          </h2>
          <ul className="space-y-2" data-id="element-270">
            <li data-id="element-271">
              <button onClick={() => handleFilter('image')} className={`w-full flex items-center p-2 rounded-md transition-colors ${isCollapsed ? 'justify-center' : ''} ${currentFilter === 'image' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-272">
                <ImageIcon className={`h-5 w-5 ${currentFilter === 'image' ? 'text-indigo-600' : 'text-purple-500'}`} data-id="element-273" />
                {!isCollapsed && <span className="ml-3" data-id="element-274">Image Generation</span>}
              </button>
            </li>
            <li data-id="element-275">
              <button onClick={() => handleFilter('chat')} className={`w-full flex items-center p-2 rounded-md transition-colors ${isCollapsed ? 'justify-center' : ''} ${currentFilter === 'chat' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-276">
                <MessageCircleIcon className={`h-5 w-5 ${currentFilter === 'chat' ? 'text-indigo-600' : 'text-green-500'}`} data-id="element-277" />
                {!isCollapsed && <span className="ml-3" data-id="element-278">Chat & Conversation</span>}
              </button>
            </li>
            <li data-id="element-279">
              <button onClick={() => handleFilter('code')} className={`w-full flex items-center p-2 rounded-md transition-colors ${isCollapsed ? 'justify-center' : ''} ${currentFilter === 'code' ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'}`} data-id="element-280">
                <CodeIcon className={`h-5 w-5 ${currentFilter === 'code' ? 'text-indigo-600' : 'text-red-500'}`} data-id="element-281" />
                {!isCollapsed && <span className="ml-3" data-id="element-282">Code Generation</span>}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </aside>;
  return <>
      {/* Mobile Menu Button */}
      <button onClick={() => setIsMobileMenuOpen(true)} className="md:hidden fixed bottom-20 left-4 z-40 bg-indigo-600 text-white p-3 rounded-full shadow-lg" data-id="element-283">
        <MenuIcon className="h-6 w-6" data-id="element-284" />
      </button>
      <MobileMenu data-id="element-285" />
      <DesktopSidebar data-id="element-286" />
    </>;
};